/**
 * Login prompt shown when an unauthenticated user taps an offer price.
 *
 * The purchase process cannot start without a SwissPass account, so this modal
 * explains why and provides a primary action to start the SwissPass login flow.
 */

import { useIonToast } from "@ionic/react";
import React, { useCallback, useMemo } from "react";
import { startSwissPassLoginWithStateSave } from "../services/auth";
import { getUIColorPalette } from "../theme/ColorPalette";
import Modal from "./Modal/Modal";

/** Popup title. */
const POPUP_TITLE = "Connexion SwissPass";

/** Popup message explaining the purchase process cannot start without login. */
const POPUP_MESSAGE = "On ne peut pas lancer la machine à billets sans connaître le voyageur. SwissPass, s'il vous plaît !";

/** Props for {@link SwissPassLoginOfferPrompt}. */
export interface SwissPassLoginOfferPromptProps {

    /** Whether the prompt is currently visible. */
    "isOpen": boolean;

    /** Called when the prompt should close (cancel or after login is started). */
    "onClose": () => void;

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Displays a branded modal explaining that the ticket purchase process requires
 * a SwissPass login. Provides a primary login action and a secondary cancel action.
 *
 * @param props - Component props.
 * @returns The login prompt modal, or `null` when closed.
 */
export const SwissPassLoginOfferPrompt: React.FC<SwissPassLoginOfferPromptProps> = ( { isOpen, onClose, isDarkUi } ) => {
    const [ present ] = useIonToast();
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    /**
     * Starts the SwissPass login flow and closes the prompt.
     *
     * On success the browser navigates away; on failure a bottom toast is shown.
     */
    const handleLogin = useCallback( () => {
        onClose();

        void startSwissPassLoginWithStateSave().catch( ( error: unknown ) => {
            const message = error instanceof Error
                ? error.message
                : "Impossible de démarrer la connexion SwissPass";
            void present( {
                message,
                "color":    "danger",
                "duration": 2500,
                "position": "bottom",
            } );
        } );
    }, [ onClose, present ] );

    return (
        <Modal
            isDarkUi={isDarkUi}
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
        >
            <div className="flex flex-col items-center gap-4 text-center px-2 pt-2">
                <h2
                    className="font-bold text-lg"
                    style={{ "color": palette.brandPrimary }}
                >
                    {POPUP_TITLE}
                </h2>
                <p
                    className="text-sm leading-relaxed"
                    style={{ "color": palette.modalCardTextPrimary }}
                >
                    {POPUP_MESSAGE}
                </p>
                <div className="flex flex-col w-full gap-3 mt-2">
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors"
                        onClick={handleLogin}
                        onMouseEnter={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.swissPassButtonBackgroundHover;
                        }}
                        onMouseLeave={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.swissPassButtonBackground;
                        }}
                        style={{ "backgroundColor": palette.swissPassButtonBackground }}
                        type="button"
                    >
                        Se connecter avec SwissPass
                    </button>
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium transition-colors"
                        onClick={onClose}
                        onMouseEnter={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.overlayBorder;
                        }}
                        onMouseLeave={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.interactiveHoverBackground;
                        }}
                        style={{
                            "backgroundColor": palette.interactiveHoverBackground,
                            "color":           palette.modalCardTextPrimary,
                        }}
                        type="button"
                    >
                        Annuler
                    </button>
                </div>
            </div>
        </Modal>
    );
};
